/** @jsxImportSource @emotion/react */
import React from 'react';
import { IBlock, TIcon } from '@datatype/type';

/**
 * @description icon block
 * 아이콘 라이브러리를 3개 혼용하며, 각 라이브러리의 prop 차이는 createIconDesign이 흡수한다.
 *   lucide   기본 (https://lucide.dev/icons/)
 *   phosphor filled가 꽉 찬 모양이어야 하는 아이콘 (fire 등) — lucide는 filled 미지원
 *   mui      브랜드 아이콘 (youtube) — lucide는 상표 문제로 미제공
 */

import {
  // auth
  Bot,
  Keyboard,
  Lightbulb,
  FlaskConical,
  LogOut,
  SquareArrowRightExit,
  Mic,
  Ellipsis,
  Square,
  UserCog,
  // membership
  Ghost,
  Smile,
  Crown,
  // navigation
  Menu,
  Search,
  X,
  LayoutGrid,
  MessageCircleMore,
  MessageCircleText,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  FolderOpen,
  Files,
  FolderKanban,
  Building,
  StickyNotePlus,
  ClockArrowDown,
  ArchiveX,
  Download,
  Share2,
  ZoomOut,
  ZoomIn,
  Hand,
  Palette,
  Scan,
  SquareDashed,
  FlagTriangleRight,
  Map as MapIcon,
  Satellite,
  MapPin,
  MapPinOff,
  MapPinPlus,
  TextAlignJustify,
  RotateCw,
  // customer
  Info,
  CircleHelp,
  Send,
  Save,
  // setting
  Settings,
  // mail
  Mail,
  MailOpen,
  // trash
  Trash2,
  Pin,
  ArchiveRestore,
  Bookmark,
  NotebookPen,
  Star,
  Tag,
  MessageSquare,
  MessageSquareText,
  Check,
  // etc
  FileQuestion,
  Package,
  Hash,
  // password
  Eye,
  EyeOff,
  // add
  CirclePlus,
  // note
  University,
  Pencil,
  SquarePen,
  // expand
  ChevronsDown,
  ChevronsUp,
  Maximize2,
  // media
  Headphones,
  HeadphoneOff,
  RotateCcw,
  SkipBack,
  SkipForward,
  Play,
  Pause,
  Captions,
  CaptionsOff,
  Languages,
  Volume2,
  VolumeX,
  NotepadText,
  // shape
  Circle,
  // flag
  Flag,
  // table chart
  ClipboardClock,
  ClipboardPlus,
  Clock,
  // lock
  Lock,
  LockOpen,
  // book
  LibraryBig,
  // repeat / swap
  RotateCwSquare,
  ArrowLeftRight,
  // fire
  Pointer,
  // lucide lab 아이콘 렌더용 (iconNode를 받는 범용 컴포넌트)
  Icon as LucideIcon,
} from 'lucide-react';

// lucide 본 패키지에 없는 lab 아이콘 (https://lucide.dev/icons/lab/house)
import { house as houseIconNode } from '@lucide/lab';

// lucide는 filled를 미지원하므로, filled가 필요한 아이콘은 phosphor 사용 (weight='fill')
// https://lucide.dev/guide/react/advanced/filled-icons
import { Fire } from '@phosphor-icons/react';

// 브랜드 아이콘(youtube) + 체크박스는 mui 사용
import YouTubeIcon from '@mui/icons-material/YouTube';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import BoxBlock from '@component/common/block/box-block';
import { useClicker } from '@hook/common/useEvent';

interface IProps {
  icon: TIcon;
  design: Pick<IBlock['design'], 'box' | 'icon'>;
  click?: IBlock['click'];
}

type TIconLibrary = 'lucide' | 'phosphor' | 'mui';

const languageOffIconNode = [
  ['path', { d: 'm5 8 6 6', key: 'language-off-1' }],
  ['path', { d: 'm4 14 6-6 2-3', key: 'language-off-2' }],
  ['path', { d: 'M2 5h12', key: 'language-off-3' }],
  ['path', { d: 'M7 2h1', key: 'language-off-4' }],
  ['path', { d: 'm22 22-5-10-5 10', key: 'language-off-5' }],
  ['path', { d: 'M14 18h6', key: 'language-off-6' }],
  ['path', { d: 'M3 3l18 18', key: 'language-off-slash' }],
] as const;

const textSnippetOffIconNode = [
  ['path', { d: 'M8 2v4', key: 'text-snippet-off-1' }],
  ['path', { d: 'M12 2v4', key: 'text-snippet-off-2' }],
  ['path', { d: 'M16 2v4', key: 'text-snippet-off-3' }],
  ['rect', { width: '16', height: '18', x: '4', y: '4', rx: '2', key: 'text-snippet-off-4' }],
  ['path', { d: 'M8 10h6', key: 'text-snippet-off-5' }],
  ['path', { d: 'M8 14h8', key: 'text-snippet-off-6' }],
  ['path', { d: 'M8 18h5', key: 'text-snippet-off-7' }],
  ['path', { d: 'M3 3l18 18', key: 'text-snippet-off-slash' }],
] as const;

/**
 * 아이콘 라이브러리마다 prop 형태가 달라 어댑터로 변환한다.
 *   lucide   { color, size } (+ filled면 fill/strokeWidth)
 *   phosphor { color, size, weight }
 *   mui      { sx: { color, fontSize } }
 *
 * lucide는 filled 변형이 없어 fill 속성으로 흉내낸다(공식 권장: fill + strokeWidth=0).
 * 단 '닫힌 단일 도형'이 아닌 아이콘은 fill 시 모양이 깨지므로, 그런 아이콘은 phosphor로 옮긴다.
 * https://lucide.dev/guide/react/advanced/filled-icons
 */
const createIconDesign = (
  library: TIconLibrary,
  kind: 'filled' | 'outlined',
  color: string | undefined,
  size: number,
): any => {
  const filled = kind === 'filled';

  switch (library) {
    case 'phosphor':
      return { color, size, weight: filled ? 'fill' : 'regular' };
    case 'mui':
      return { sx: { color, fontSize: `${size}px` } };
    case 'lucide':
    default:
      return filled ? { color, size, fill: color, strokeWidth: 0 } : { color, size };
  }
};

const IconBlock: React.FC<IProps> = ({ icon, design, click }) => {
  let Icon;

  const curKind =
    design?.icon?.checked && design?.icon?.kind
      ? design?.icon?.kind === 'filled'
        ? 'outlined'
        : 'filled'
      : design?.icon?.kind === 'filled'
        ? 'filled'
        : 'outlined';

  const checked = design.icon?.checked;
  const iconSize = design.icon?.size;
  const checkedColor = design.icon?.checkedColor;
  const color = design.icon?.color;

  // 모든 크기를 픽셀로 매핑
  let size: number;
  switch (iconSize) {
    case 'tiny':
      size = 16;
      break;
    case 'small':
      size = 20;
      break;
    case 'medium':
      size = 24;
      break;
    case 'large':
      size = 35;
      break;
    case 'big':
      size = 48;
      break;
    default:
      size = 24;
  }

  const iconColor = checked ? checkedColor : color;

  const lucideDesign = createIconDesign('lucide', curKind, iconColor, size);
  const phosphorDesign = createIconDesign('phosphor', curKind, iconColor, size);
  const muiDesign = createIconDesign('mui', curKind, iconColor, size);

  switch (icon) {
    // auth
    case 'logout':
      Icon = <LogOut {...lucideDesign} />;
      break;
    case 'square-arrow-right-exit':
      Icon = <SquareArrowRightExit {...lucideDesign} />;
      break;
    case 'account':
      Icon = <UserCog {...lucideDesign} />;
      break;
    // membership
    case 'ghost':
      Icon = <Ghost {...lucideDesign} />;
      break;
    case 'smile':
      Icon = <Smile {...lucideDesign} />;
      break;
    case 'crown':
      Icon = <Crown {...lucideDesign} />;
      break;
    // navigation
    case 'close':
      Icon = <X {...lucideDesign} />;
      break;
    case 'back':
      Icon = <ChevronLeft {...lucideDesign} />;
      break;
    case 'forward':
      Icon = <ChevronRight {...lucideDesign} />;
      break;
    case 'left':
      Icon = <ChevronLeft {...lucideDesign} />;
      break;
    case 'right':
      Icon = <ChevronRight {...lucideDesign} />;
      break;
    case 'up':
      Icon = <ChevronUp {...lucideDesign} />;
      break;
    case 'down':
      Icon = <ChevronDown {...lucideDesign} />;
      break;
    case 'files':
      Icon = <Files {...lucideDesign} />;
      break;
    case 'sort':
      Icon = <ClockArrowDown {...lucideDesign} />;
      break;
    case 'add-file':
      Icon = <StickyNotePlus {...lucideDesign} />;
      break;
    case 'building':
      Icon = <Building {...lucideDesign} />;
      break;
    case 'folder-kanban':
      Icon = <FolderKanban {...lucideDesign} />;
      break;
    case 'folder-open':
      Icon = <FolderOpen {...lucideDesign} />;
      break;
    case 'archive-x':
      Icon = <ArchiveX {...lucideDesign} />;
      break;
    case 'download':
      Icon = <Download {...lucideDesign} />;
      break;
    case 'share':
      Icon = <Share2 {...lucideDesign} />;
      break;
    case 'zoom-out':
      Icon = <ZoomOut {...lucideDesign} />;
      break;
    case 'zoom-in':
      Icon = <ZoomIn {...lucideDesign} />;
      break;
    case 'move':
      Icon = <Hand {...lucideDesign} />;
      break;
    case 'palette':
      Icon = <Palette {...lucideDesign} />;
      break;
    case 'scan':
      Icon = <Scan {...lucideDesign} />;
      break;
    case 'square-dashed':
      Icon = <SquareDashed {...lucideDesign} />;
      break;
    case 'flag-triangle-right':
      Icon = <FlagTriangleRight {...lucideDesign} />;
      break;
    case 'map-pin-plus':
      Icon = <MapPinPlus {...lucideDesign} />;
      break;
    case 'clipboard-plus':
      Icon = <ClipboardPlus {...lucideDesign} />;
      break;
    case 'text-align-justify':
      Icon = <TextAlignJustify {...lucideDesign} />;
      break;
    case 'map-pin-off':
      Icon = <MapPinOff {...lucideDesign} />;
      break;
    case 'map':
      Icon = <MapIcon {...lucideDesign} />;
      break;
    case 'satellite':
      Icon = <Satellite {...lucideDesign} />;
      break;
    case 'map-pin':
      Icon = <MapPin {...lucideDesign} />;
      break;
    case 'rotate-left':
      Icon = <RotateCcw {...lucideDesign} />;
      break;
    case 'rotate-right':
      Icon = <RotateCw {...lucideDesign} />;
      break;
    case 'menu':
      Icon = <Menu {...lucideDesign} />;
      break;
    case 'home':
      Icon = <LucideIcon iconNode={houseIconNode} {...lucideDesign} />;
      break;
    case 'window':
      Icon = <LayoutGrid {...lucideDesign} />;
      break;
    case 'feed':
      Icon = <MessageCircleMore {...lucideDesign} />;
      break;
    case 'bot':
      Icon = <Bot {...lucideDesign} />;
      break;
    case 'mic':
      Icon = <Mic {...lucideDesign} />;
      break;
    case 'keyboard':
      Icon = <Keyboard {...lucideDesign} />;
      break;
    case 'lightbulb':
      Icon = <Lightbulb {...lucideDesign} />;
      break;
    case 'flask':
      Icon = <FlaskConical {...lucideDesign} />;
      break;
    case 'today':
      Icon = <CalendarDays {...lucideDesign} />;
      break;
    // customer
    case 'info':
      Icon = <Info {...lucideDesign} />;
      break;
    case 'help':
      Icon = <CircleHelp {...lucideDesign} />;
      break;
    case 'send':
      Icon = <Send {...lucideDesign} />;
      break;
    case 'save':
      Icon = <Save {...lucideDesign} />;
      break;
    // setting
    case 'setting':
      Icon = <Settings {...lucideDesign} />;
      break;
    // mail
    case 'mail':
      Icon = <Mail {...lucideDesign} />;
      break;
    case 'mail-unread':
      Icon = <MailOpen {...lucideDesign} />;
      break;
    // 체크박스는 filled/outlined가 아니라 아이콘 자체가 바뀐다 (빈 네모 ↔ 체크된 네모)
    case 'check-box':
      Icon = curKind === 'outlined' ? <CheckBoxOutlineBlankIcon {...muiDesign} /> : <CheckBoxIcon {...muiDesign} />;
      break;
    case 'check':
      Icon = <Check {...lucideDesign} />;
      break;
    // trash
    case 'pin':
      Icon = <Pin {...lucideDesign} />;
      break;
    case 'trash':
      Icon = <Trash2 {...lucideDesign} />;
      break;
    case 'untrash':
      Icon = <ArchiveRestore {...lucideDesign} />;
      break;
    case 'star':
      Icon = <Star {...lucideDesign} />;
      break;
    case 'bookmark':
      Icon = <Bookmark {...lucideDesign} />;
      break;
    case 'label':
      Icon = <Tag {...lucideDesign} />;
      break;
    case 'memo':
      Icon = <MessageSquare {...lucideDesign} />;
      break;
    case 'memo-text':
      Icon = <MessageSquareText {...lucideDesign} />;
      break;
    case 'quiz':
      Icon = <FileQuestion {...lucideDesign} />;
      break;
    case 'hash':
      Icon = <Hash {...lucideDesign} />;
      break;
    case 'inventory':
      Icon = <Package {...lucideDesign} />;
      break;
    case 'search':
      Icon = <Search {...lucideDesign} />;
      break;
    // password
    case 'visibility-on':
      Icon = <Eye {...lucideDesign} />;
      break;
    case 'visibility-off':
      Icon = <EyeOff {...lucideDesign} />;
      break;
    // add
    case 'add':
      Icon = <CirclePlus {...lucideDesign} />;
      break;
    // media
    case 'headphone-on':
      Icon = <Headphones {...lucideDesign} />;
      break;
    case 'headphone-off':
      Icon = <HeadphoneOff {...lucideDesign} />;
      break;
    case 'play':
      Icon = <Play {...lucideDesign} />;
      break;
    case 'pause':
      Icon = <Pause {...lucideDesign} />;
      break;
    case 'replay':
      Icon = <RotateCcw {...lucideDesign} />;
      break;
    case 'rewind':
      Icon = <SkipBack {...lucideDesign} />;
      break;
    case 'fast-forward':
      Icon = <SkipForward {...lucideDesign} />;
      break;
    case 'caption-on':
      Icon = <Captions {...lucideDesign} />;
      break;
    case 'caption-off':
      Icon = <CaptionsOff {...lucideDesign} />;
      break;
    case 'language':
      Icon = <Languages {...lucideDesign} />;
      break;
    case 'language-off':
      Icon = <LucideIcon iconNode={languageOffIconNode} {...lucideDesign} />;
      break;
    case 'volume-on':
      Icon = <Volume2 {...lucideDesign} />;
      break;
    case 'volume-off':
      Icon = <VolumeX {...lucideDesign} />;
      break;
    case 'message-circle-text':
      Icon = <MessageCircleText {...lucideDesign} />;
      break;
    case 'text-snippet':
      Icon = <NotepadText {...lucideDesign} />;
      break;
    case 'text-snippet-off':
      Icon = <LucideIcon iconNode={textSnippetOffIconNode} {...lucideDesign} />;
      break;
    // lucide에 브랜드 아이콘이 없어 mui 사용
    case 'youtube':
      Icon = <YouTubeIcon {...muiDesign} />;
      break;
    // flag
    case 'flag':
      Icon = <Flag {...lucideDesign} />;
      break;
    // shape
    case 'circle':
      Icon = <Circle {...lucideDesign} />;
      break;
    // table chart
    case 'table-chart':
      Icon = <ClipboardClock {...lucideDesign} />;
      break;
    case 'clock':
      Icon = <Clock {...lucideDesign} />;
      break;
    // note
    case 'edit-note':
      Icon = <University {...lucideDesign} />;
      break;
    case 'pen':
      Icon = <Pencil {...lucideDesign} />;
      break;
    case 'edit':
      Icon = <SquarePen {...lucideDesign} />;
      break;
    // repeat / swap
    case 'repeat':
      Icon = <RotateCwSquare {...lucideDesign} />;
      break;
    case 'swap':
      Icon = <ArrowLeftRight {...lucideDesign} />;
      break;
    // fire
    case 'fire':
      Icon = <Fire {...phosphorDesign} />;
      break;
    case 'touch-app':
      Icon = <Pointer {...lucideDesign} />;
      break;
    // expand
    case 'more':
      Icon = <ChevronsDown {...lucideDesign} />;
      break;
    case 'ellipsis':
      Icon = <Ellipsis {...lucideDesign} />;
      break;
    case 'stop':
      Icon = <Square {...lucideDesign} />;
      break;
    case 'less':
      Icon = <ChevronsUp {...lucideDesign} />;
      break;
    case 'maximize':
      Icon = <Maximize2 {...lucideDesign} />;
      break;
    // lock
    case 'lock':
      Icon = <Lock {...lucideDesign} />;
      break;
    case 'unlock':
      Icon = <LockOpen {...lucideDesign} />;
      break;
    // book
    case 'notebook-pen':
      Icon = <NotebookPen {...lucideDesign} />;
      break;
    case 'book':
      Icon = <LibraryBig {...lucideDesign} />;
      break;
    default:
      Icon = <div></div>;
  }

  const clicker = useClicker(click?.handler, click?.delay, click?.mode);

  return (
    <BoxBlock design={{ box: design.box }} onClick={clicker}>
      {Icon}
    </BoxBlock>
  );
};

export default IconBlock;
